import { FingerType, FingerConfig, TrainingDay } from '@/types';

// Core finger to key mapping as specified in the requirements
export const FINGER_KEY_MAPPING: Record<FingerType, string[]> = {
  // Left Hand
  leftThumb: ['SPACE', 'Z', 'X', 'C'], // Big finger in Russian terminology
  leftPointing: ['G', 'F', 'V'],
  leftMiddle: ['D', 'R', 'T'],
  leftRing: ['E', 'S'], // RingFinger
  leftPinky: ['A', 'Q', 'W'], // Pinky

  // Right Hand  
  rightThumb: ['M', 'SPACE'], // Big finger
  rightPointing: ['H', 'N', 'B'],
  rightMiddle: ['J', 'Y', 'U'],
  rightRing: ['I', 'K'], // RingFinger
  rightPinky: ['P', 'O', 'L'], // Pinky
};

// Reverse mapping: key to finger
export const KEY_FINGER_MAPPING: Record<string, FingerType> = {};

// Build reverse mapping
Object.entries(FINGER_KEY_MAPPING).forEach(([finger, keys]) => {
  keys.forEach(key => {
    KEY_FINGER_MAPPING[key] = finger as FingerType;
  });
});

// Training progression matrix based on the provided spreadsheet
export const TRAINING_DAYS: TrainingDay[] = [
  // Week 1: Finger introduction with BOTH HANDS by default
  {
    day: 1,
    fingerConfig: {
      activeFingersLeft: ['leftPinky'],
      activeFingersRight: ['rightPinky'], // FIXED: Both pinkies for pinky training
      letterComplexity: { min: 1, max: 2 }
    },
    description: "Pinky training (A, Q, W + P, O, L)",
    phase: 'introduction'
  },
  {
    day: 2,
    fingerConfig: {
      activeFingersLeft: ['leftMiddle'],
      activeFingersRight: ['rightMiddle'], // FIXED: Both middle fingers
      letterComplexity: { min: 1, max: 2 }
    },
    description: "Middle finger training (D, R, T + J, Y, U)",
    phase: 'introduction'
  },
  {
    day: 3,
    fingerConfig: {
      activeFingersLeft: ['leftRing'],
      activeFingersRight: ['rightRing'], // FIXED: Both ring fingers
      letterComplexity: { min: 1, max: 3 }
    },
    description: "Ring finger training (E, S + I, K)",
    phase: 'introduction'
  },
  {
    day: 4,
    fingerConfig: {
      activeFingersLeft: ['leftPointing'],
      activeFingersRight: ['rightPointing'], // FIXED: Both pointing fingers
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Pointing finger training (G, F, V, C + H, N, B)",
    phase: 'building'
  },
  {
    day: 5,
    fingerConfig: {
      activeFingersLeft: ['leftThumb'],
      activeFingersRight: ['rightThumb'], // FIXED: Both thumbs
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Thumb training (Space + M)",
    phase: 'building'
  },
  {
    day: 6,
    fingerConfig: {
      activeFingersLeft: ['leftPinky', 'leftMiddle'],
      activeFingersRight: ['rightPinky', 'rightMiddle'], // Combination: Pinky + Middle both hands
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Pinky + Middle finger combination",
    phase: 'building'
  },
  {
    day: 7,
    fingerConfig: {
      activeFingersLeft: ['leftRing', 'leftPointing'],
      activeFingersRight: ['rightRing', 'rightPointing'], // Combination: Ring + Pointing both hands
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Ring + Pointing finger combination",
    phase: 'building'
  },
  {
    day: 8,
    fingerConfig: {
      activeFingersLeft: ['leftPinky', 'leftRing'],
      activeFingersRight: ['rightPinky', 'rightRing'], // Combination: Pinky + Ring both hands
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Pinky + Ring finger combination",
    phase: 'building'
  },
  {
    day: 9,
    fingerConfig: {
      activeFingersLeft: ['leftMiddle', 'leftPointing'],
      activeFingersRight: ['rightMiddle', 'rightPointing'], // Combination: Middle + Pointing both hands
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Middle + Pointing finger combination",
    phase: 'building'
  },
  {
    day: 10,
    fingerConfig: {
      activeFingersLeft: ['leftPinky', 'leftPointing'],
      activeFingersRight: ['rightPinky', 'rightPointing'], // Combination: Pinky + Pointing both hands
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Pinky + Pointing finger combination",
    phase: 'building'
  },
  {
    day: 11,
    fingerConfig: {
      activeFingersLeft: ['leftMiddle', 'leftRing'],
      activeFingersRight: ['rightMiddle', 'rightRing'], // Combination: Middle + Ring both hands
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Middle + Ring finger combination",
    phase: 'building'
  },
  {
    day: 12,
    fingerConfig: {
      activeFingersLeft: ['leftThumb', 'leftPointing'],
      activeFingersRight: ['rightThumb', 'rightPointing'], // Combination: Thumb + Pointing both hands
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Thumb + Pointing finger combination",
    phase: 'building'
  },
  {
    day: 13,
    fingerConfig: {
      activeFingersLeft: ['leftThumb'],
      activeFingersRight: ['rightMiddle'],
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Left thumb + Right middle combination",
    phase: 'building'
  },
  {
    day: 14,
    fingerConfig: {
      activeFingersLeft: ['leftPinky'],
      activeFingersRight: ['rightMiddle'],
      letterComplexity: { min: 2, max: 4 }
    },
    description: "Left pinky + Right middle combination",
    phase: 'building'
  },
  {
    day: 15,
    fingerConfig: {
      activeFingersLeft: ['leftThumb'],
      activeFingersRight: ['rightRing'],
      letterComplexity: { min: 3, max: 5 }
    },
    description: "Left thumb + Right ring combination",
    phase: 'combination'
  },
  // Days 16-31 continue with increasing complexity
  // 3 из 5, 4 из 5, 5 из 5 progression
  ...Array.from({ length: 16 }, (_, i) => ({
    day: 16 + i,
    fingerConfig: {
      activeFingersLeft: (i < 8 ?
        ['leftPinky', 'leftRing', 'leftMiddle'] :
        ['leftPinky', 'leftRing', 'leftMiddle', 'leftPointing']) as FingerType[],
      activeFingersRight: (i < 8 ?
        ['rightPointing', 'rightMiddle'] :
        ['rightPointing', 'rightMiddle', 'rightRing']) as FingerType[],
      letterComplexity: {
        min: i < 4 ? 3 : i < 8 ? 4 : 4,
        max: i < 4 ? 5 : i < 8 ? 5 : 5
      }
    },
    description: `Progressive multi-finger training - Day ${16 + i}`,
    phase: i < 12 ? 'combination' as const : 'mastery' as const
  }))
];

// Helper functions
export const getKeysForFingers = (fingers: FingerType[]): string[] => {
  const keys: string[] = [];
  fingers.forEach(finger => {
    keys.push(...FINGER_KEY_MAPPING[finger]);
  });
  return [...new Set(keys)]; // Remove duplicates
};

export const getFingerForKey = (key: string): FingerType | null => {
  return KEY_FINGER_MAPPING[key.toUpperCase()] || null;
};

export const getTrainingDay = (dayNumber: number): TrainingDay | null => {
  return TRAINING_DAYS.find(day => day.day === dayNumber) || null;
};

export const getAllActiveFingers = (config: FingerConfig): FingerType[] => {
  return [...config.activeFingersLeft, ...config.activeFingersRight];
};

// Default finger positions for calibration (relative to hand image)
export const DEFAULT_FINGER_POSITIONS = {
  left: {
    leftPinky: { x: 0.15, y: 0.8 },
    leftRing: { x: 0.35, y: 0.75 },
    leftMiddle: { x: 0.5, y: 0.7 },
    leftPointing: { x: 0.65, y: 0.75 },
    leftThumb: { x: 0.8, y: 0.9 }
  },
  right: {
    rightThumb: { x: 0.2, y: 0.9 },
    rightPointing: { x: 0.35, y: 0.75 },
    rightMiddle: { x: 0.5, y: 0.7 },
    rightRing: { x: 0.65, y: 0.75 },
    rightPinky: { x: 0.85, y: 0.8 }
  }
};
